import React from 'react'
import { Section, SectionContent, SectionItem, SubTitle } from '../../components/Shared'
import { getMethod } from '../../util/Managed'
import { genSectionItems } from '../../util/SectionItems'
import { asSimpleDateTime, formatCurrency } from '../../util/Util'
import { RADOM_COLORS } from '../../util/Constants'
import PaymentMethodDisplay from '../../components/PaymentMethodDisplay'
import { PaymentDetailsSection } from '../../components/PaymentDetails'
import { Customer, Deposit, ISubscription } from '../../state/Radom'

interface CustomerTransactionsProps {
  customer?: Customer
  subscriptions: ISubscription[]
  subscriptionColumnCount: number
  deposits: Deposit[]
  depositColumnCount: number
}

const CustomerTransactions = ({
  customer,
  subscriptions,
  subscriptionColumnCount,
  deposits,
  depositColumnCount
}: CustomerTransactionsProps): JSX.Element => {
  const payments = customer?.payments ?? []

  if (
    payments.length === 0 &&
    (!subscriptions || subscriptions.length === 0) &&
    (!deposits || deposits.length === 0)
  ) {
    return <div style={{ padding: 20, color: RADOM_COLORS.GRAY4, textAlign: 'center' }}>
        No transactions found
    </div>
  }
  return (
    <>
      {
      // ---------------------------------------------------------------------------------------------------------------
      // Payment info
      // ---------------------------------------------------------------------------------------------------------------
        payments.length > 0 &&

      <Section>
        <SubTitle style={{ fontSize: 18 }}>
          Payments
        </SubTitle>
        {
          payments.map((p, i) => {
            if (p.managed) {
              return <PaymentDetailsSection
                short
                key={i}
                payment={{ managed: p.managed }}
                netPayment={(Number(p.managed.amount) - Number(p.managed.networkFee)) * 0.995} />
            } else if (p.openBanking) {
              return <PaymentDetailsSection
                short
                key={i}
                payment={{ openBanking: p.openBanking }}
                netPayment={0} />
            } else {
              return null
            }
          })
        }
      </Section>
      }

      {
      // ---------------------------------------------------------------------------------------------------------------
      // Subscriptions
      // ---------------------------------------------------------------------------------------------------------------
        subscriptions && subscriptions.length > 0 &&
      <Section style={{
        backgroundColor: 'var(--surface, #ffffff)'
      }}>
        <SubTitle style={{ fontSize: 18 }}>
          Subscriptions
        </SubTitle>
        {
          subscriptions.reduce<React.ReactElement[]>((acc, sub, idx) => {
            const items = <Section style={{ backgroundColor: 'var(--surface, #ffffff)' }}>
              <SectionContent numColumns={subscriptionColumnCount}>
                {genSectionItems([
                  { heading: 'Subscription Id', content: sub.id },
                  { heading: 'Created', content: asSimpleDateTime(sub.createdAt) },
                  { heading: 'Status', content: sub.status },
                  { heading: 'Checkout Type', content: sub.associatedEntityType },
                  { heading: 'Product', content: sub.tags?.name },

                  sub.cancelledAt
                    ? { heading: 'Cancelled At', content: asSimpleDateTime(sub.cancelledAt) }
                    : sub.expiredAt
                      ? { heading: 'Expired At', content: asSimpleDateTime(sub.expiredAt) }
                      : { heading: '', content: null },

                  sub.subscriptionType
                    ? sub.subscriptionType.automatedEVMSubscription
                      ? { heading: 'Email Address', content: sub.subscriptionType.automatedEVMSubscription?.emailAddress }
                      : { heading: 'Email Address', content: sub.subscriptionType.emailInvoiceSubscription?.emailAddress }
                    : { heading: 'Email Address', content: null },

                  { heading: 'Next Billing Date', content: asSimpleDateTime(sub.nextBillingDateAt) },
                  <SectionItem key={idx}>
                    <SubTitle>Amount</SubTitle>
                    <PaymentMethodDisplay
                      method={{ network: sub.network, token: sub.token }}
                      excludeName
                      disableTooltip={false} />
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                      {formatCurrency(Number(sub.amount), sub.currency)}
                      <span style={{ fontWeight: 300, opacity: 0.75 }}>{sub.period}</span>
                    </div>
                  </SectionItem>
                ], subscriptionColumnCount)}
              </SectionContent>
            </Section>

            return acc.concat(items)
          }, [])
        }
      </Section>
      }

      {
      // ---------------------------------------------------------------------------------------------------------------
      // Deposits
      // ---------------------------------------------------------------------------------------------------------------
        deposits && deposits.length > 0 &&
      <Section style={{
        backgroundColor: 'var(--surface, #ffffff)'
      }}>
        <SubTitle style={{ fontSize: 18 }}>
          Deposits
        </SubTitle>
        {
          deposits.reduce<React.ReactElement[]>((acc, dep, idx) => {
            const items = <Section style={{ backgroundColor: 'var(--surface, #ffffff)' }}>
              <SectionContent numColumns={depositColumnCount}>
                {genSectionItems([
                  { heading: 'Deposit Id', content: dep.id },
                  { heading: 'Type', content: dep.depositMethod.Crypto ? 'Crypto deposit' : 'Fiat deposit' },
                  { heading: 'Deposited', content: asSimpleDateTime(dep.depositedAt) },
                  <SectionItem key={idx}>
                    <SubTitle>Net Amount</SubTitle>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <img src={getMethod(
                        dep.depositMethod.Crypto?.network ||
                        dep.depositMethod.Fiat?.onrampNetwork || '',
                        dep.depositMethod.Crypto?.token ||
                        dep.depositMethod.Fiat?.onrampToken
                      ).logo}
                      style={{
                        width: 16,
                        height: 16
                      }}
                      />
                      {dep.netAmount}
                    </div>
                  </SectionItem>,
                  <SectionItem key={idx + 1}>
                    <SubTitle>Gross Amount</SubTitle>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <img src={getMethod(
                        dep.depositMethod.Crypto?.network ||
                        dep.depositMethod.Fiat?.onrampNetwork || '',
                        dep.depositMethod.Crypto?.token ||
                        dep.depositMethod.Fiat?.onrampToken
                      ).logo}
                      style={{
                        width: 16,
                        height: 16
                      }}
                      />
                      {dep.grossAmount}
                    </div>
                  </SectionItem>,
                  <SectionItem key={idx + 2}>
                    <SubTitle>Fee</SubTitle>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <img src={getMethod(
                        dep.depositMethod.Crypto?.network ||
                        dep.depositMethod.Fiat?.onrampNetwork || '',
                        dep.depositMethod.Crypto?.token ||
                        dep.depositMethod.Fiat?.onrampToken
                      ).logo}
                      style={{
                        width: 16,
                        height: 16
                      }}
                      />
                      {dep.fee}
                    </div>
                  </SectionItem>,
                  ...(dep.depositMethod.Fiat && dep.fiatGrossAmount
                    ? [
                      <SectionItem key={idx + 2}>
                        <SubTitle>Fiat Gross Amount</SubTitle>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <img src={getMethod(
                            'Fiat',
                            dep.depositMethod.Fiat.fiatCurrency
                          ).logo}
                          style={{
                            width: 16,
                            height: 16
                          }}
                          />
                          {dep.fiatGrossAmount}
                        </div>
                      </SectionItem>
                    ]
                    : [])
                ], depositColumnCount)}
              </SectionContent>
            </Section>

            return acc.concat(items)
          }, [])
        }
      </Section>
      }

    </>
  )
}

export default CustomerTransactions
